import { useState, useEffect } from 'react'
import {
  useAddPlantMutation,
  useLazyGetProductsQuery,
  useUpdatePlantMutation,
  useUpdatePlantStatusMutation,
} from '../../api/app/PlantApiService'
import { useTranslation } from 'react-i18next'
import { showToast } from '../../components/common/toast/Toast'
import {
  GenericAPIResponse,
  SerializedError,
} from '../../interface/response/CommonResponse'
import { REQUEST_STATUS } from '../../utils/constants/RequestCommonConstant'
import { PlantInfo } from '../../interface/response/PlantResponse'
import {
  PlantRequest,
  UpdatePlantRequest,
  UpdatePlantStatusRequest,
} from '../../interface/request/PlantRequest'
import { useLazyGetAssociatedVendorsQuery } from '../../api/app/VendorApiService'
import { ProductData } from '../../interface/response/ProductResponse'
import { multiDropdown } from '../../components/common/formComponent/FormComponentProps'
import { ERROR } from '../../utils/constants/UIConstants'

const usePlantDetails = () => {
  const { t } = useTranslation(['translation'])
  const [getAssociatedVendorList, associatedVendorList] =
    useLazyGetAssociatedVendorsQuery()
  const [getProductList, productList] = useLazyGetProductsQuery()
  const [showPlantDetailsForm, setShowPlantDetailsForm] =
    useState<boolean>(false)
  const [showRejectPlantAlert, setShowRejectPlantAlert] = useState(false)
  const [verifiedDetails, setVerifiedDetails] = useState<boolean>(false)
  const [showApprovePlantAlert, setShowApprovePlantAlert] = useState(false)
  const [addPlant] = useAddPlantMutation()
  const [updatePlant] = useUpdatePlantMutation()
  const [updatePlantStatus] = useUpdatePlantStatusMutation()

  const updatePlantFormStatus = (status: boolean) => {
    setShowPlantDetailsForm(status)
  }
  const updateVerifiedCheckBoxStatus = (status: boolean) => {
    setVerifiedDetails(status)
  }
  useEffect(() => {
    getAssociatedVendorList([])
    updateVerifiedCheckBoxStatus(false)
  }, [getAssociatedVendorList, getProductList])

  const getFormItemValue = (data: PlantInfo, key: string) => {
    if (data) {
      // if (key === 'productNames') {
      //   const result = data?.productNames.map(
      //     (productObj: any) => productObj.productName,
      //   )
      //   return String(result.join(', ') ?? '')
      // }
      return data?.[key as keyof PlantInfo] ?? ''
    } else return ''
  }

  const displayErrorToast = (message: string, code: number | string) => {
    showToast(`${message}(${code})`, 'error')
  }
  const getFormattedProductTypes = (): multiDropdown[] | undefined => {
    if (productList?.data) {
      return productList?.data?.map((product: ProductData) => {
        return {
          label: product?.productName,
          value: product?.productCode,
        }
      })
    }
  }
  const selectedProductList = (productNames: string[] | undefined) => {
    if (productNames) {
      return productNames?.map((product: any) => {
        return {
          label: product?.productName,
          value: product?.productCode,
        }
      })
    }
  }
  const createPlant = (values: PlantRequest) => {
    addPlant(values)
      .unwrap()
      .then((response: GenericAPIResponse) => {
        if (response?.requestStatus) {
          showToast(t(`messages.${response?.code}`))
        } else {
          displayErrorToast(t(`messages.${response?.code}`), response?.code)
        }
      })
      .catch((error: SerializedError) => {
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
      })
  }

  const updatePlantInfo = (values: UpdatePlantRequest) => {
    updatePlant(values)
      .unwrap()
      .then((response: GenericAPIResponse) => {
        if (response?.requestStatus) {
          showToast(t(`messages.${response?.code}`))
        } else {
          displayErrorToast(t(`messages.${response?.code}`), response?.code)
        }
      })
      .catch((error: SerializedError) => {
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
      })
  }

  const handleOnClickReject = () => {
    setShowRejectPlantAlert(true)
  }
  const handleOnClickApprove = () => {
    setShowApprovePlantAlert(true)
  }
  const approvePlantStatus = (plantCode: string) => {
    const approvalRequest: UpdatePlantStatusRequest = {
      plantCode: plantCode,
      status: REQUEST_STATUS.APPROVED,
    }
    updatePlantStatus(approvalRequest)
      .unwrap()
      .then((response: GenericAPIResponse) => {
        if (response?.requestStatus) {
          showToast(t(`messages.${response?.code}`))
        } else {
          displayErrorToast(t(`messages.${response?.code}`), response?.code)
        }
        setShowApprovePlantAlert(false)
      })
      .catch((error: SerializedError) => {
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
        setShowApprovePlantAlert(false)
      })
  }
  const rejectPlantStatus = (value: UpdatePlantStatusRequest) => {
    updatePlantStatus(value)
      .unwrap()
      .then((response: GenericAPIResponse) => {
        if (response?.requestStatus) {
          showToast(t(`messages.${response?.code}`))
        } else {
          displayErrorToast(t(`messages.${response?.code}`), response?.code)
        }
        setShowRejectPlantAlert(false)
      })
      .catch((error: SerializedError) => {
        displayErrorToast(t(`messages.${error?.status}`), error?.status)
        setShowRejectPlantAlert(false)
      })
  }

  return {
    verifiedDetails,
    associatedVendorList,
    showRejectPlantAlert,
    showApprovePlantAlert,
    showPlantDetailsForm,
    getFormattedProductTypes,
    updatePlantFormStatus,
    getFormItemValue,
    createPlant,
    updatePlantInfo,
    handleOnClickReject,
    handleOnClickApprove,
    approvePlantStatus,
    rejectPlantStatus,
    setShowRejectPlantAlert,
    setShowApprovePlantAlert,
    updateVerifiedCheckBoxStatus,
  }
}

export default usePlantDetails
